import { FunctionComponent, useCallback, useRef } from 'react'
import { Checkbox, Input, Text, getTheme } from '@conexasaude/conexa-hero'
import Modal from '@/components/Modal'
import { useModalContext } from '@/components/Modal/contexts/ModalContext'
import useStyles from './styles'
import { MinorsTermFormData, MinorsTermModalProps } from './types'
import { useForm } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import { minorsTermSchema } from '../../validations/MinorsTermModalSchema'
import { EVENT_NAME, URL_MINORS_TERM } from './constants'
import { useSendMinorsTermData } from '../../queries'
import { showErrorToast } from '@/modules/Activation/utils/requisitionErrors'
import { usePatientStore } from '@/modules/Activation/store/PatientStore/patient.store'
import { openExternalLink } from '@/utils/fnUtils'
import { useNavigation } from '@/hooks/useNavigation'
import { AnalyticEvent } from '@/modules/Activation/types/en-us'
import { useFirebaseContext } from '@/modules/Firebase/contexts/FirebaseContext'
import { SEND_TO } from '@/modules/Signup/constants'
import { getDigitsFromString } from '@/utils/getDigitsFromString'
import { sha256Hash } from '@conexasaude/analytics'
import { useLocation } from 'react-router-dom'
import { useGetAllUsers } from '@/modules/Activation/queries/useGetAllUsers'
import { useAuthContext } from '../../contexts/AuthContext'
import { formatCpfNumber } from '@/utils/masks/cpf'

const MinorsTermModal: FunctionComponent<MinorsTermModalProps> = ({
  checkTermsAndConditionsAcceptance,
}) => {
  const styles = useStyles()
  const theme = getTheme()
  const navigate = useNavigation()
  const { pathname } = useLocation()
  const { closeModal } = useModalContext()
  const formRef = useRef<HTMLFormElement>(null)
  const { logAnalyticsEvent } = useFirebaseContext()
  const { mutateAsync, isLoading } = useSendMinorsTermData()
  const { patient, holder } = usePatientStore()
  const { mutateAsync: getAllUsers, isLoading: isLoadingGetAllUsers } =
    useGetAllUsers()
  const { signIn } = useAuthContext()
  const form = useForm<MinorsTermFormData>({
    resolver: zodResolver(minorsTermSchema),
    mode: 'onChange',
    defaultValues: {
      guardianCpf: formatCpfNumber(holder?.cpf) || '',
      guardianName: holder?.name || '',
    },
  })

  const normalizedCPF = getDigitsFromString(holder?.cpf)
  const hashedCPF = sha256Hash(normalizedCPF)

  const openTermUrl = () => {
    openExternalLink(URL_MINORS_TERM)
  }

  const settingUpAnalyticsEvent = useCallback(
    (eventDefinitions: AnalyticEvent) => {
      const routePath =
        pathname?.split('/')?.[2] || pathname?.split('/')?.[1] || ''
      const eventName = eventDefinitions?.eventName || EVENT_NAME[routePath]
      logAnalyticsEvent(eventName, {
        interaction: `${eventDefinitions?.interaction}`,
        options: {
          sendTo: SEND_TO,
        },
        ...(eventDefinitions?.cnx_user_id && {
          cnx_user_id: eventDefinitions?.cnx_user_id,
        }),
        ...(eventDefinitions?.user_id && {
          user_id: eventDefinitions?.user_id,
        }),
      })
    },
    [logAnalyticsEvent, pathname],
  )

  const acceptTerms = async () => {
    settingUpAnalyticsEvent({
      interaction: 'continue_click',
      ...(hashedCPF && { cnx_user_id: hashedCPF }),
      ...(hashedCPF && { user_id: hashedCPF }),
    })

    const payloadData = {
      ...form?.getValues(),
      idPatientDependent: patient.id,
    }
    try {
      await mutateAsync(payloadData)
      const users = await getAllUsers(holder?.token)
      if (users?.length) signIn(users)
      if (checkTermsAndConditionsAcceptance) checkTermsAndConditionsAcceptance()
      closeModal()
      navigate('/ativacao/conta-dependente-ativada')
    } catch (error) {
      showErrorToast(error)
    }
  }

  const closeModalAndAnalyticsEvent = () => {
    settingUpAnalyticsEvent({
      interaction: 'cancel_click',
      ...(hashedCPF && { cnx_user_id: hashedCPF }),
      ...(hashedCPF && { user_id: hashedCPF }),
    })
    closeModal()
  }

  const trackMinorsTermClick = () => {
    settingUpAnalyticsEvent({
      interaction: 'accept_term_click',
      ...(hashedCPF && { cnx_user_id: hashedCPF }),
      ...(hashedCPF && { user_id: hashedCPF }),
    })
  }

  const trackOnDimiss = () => {
    settingUpAnalyticsEvent({
      interaction: 'close_click',
      ...(hashedCPF && { cnx_user_id: hashedCPF }),
      ...(hashedCPF && { user_id: hashedCPF }),
    })
  }

  return (
    <Modal
      id="MINORS_TERM"
      negativeAction={closeModalAndAnalyticsEvent}
      negativeActionLabel="Cancelar"
      primaryAction={acceptTerms}
      primaryActionLabel="Continuar"
      disabledPrimaryAction={!form?.formState?.isValid}
      busyPrimaryAction={isLoading || isLoadingGetAllUsers}
      withCloseButton
      onDismiss={trackOnDimiss}
    >
      <div css={styles.modalContent}>
        <Text variant="paragraphSmall" css={styles.subtitle}>
          Este formulário deve ser preenchido pelo responsável legal.
        </Text>
        <Text variant="paragraphSmall">
          Ao aceitar este termo, você confirma que a criança ou adolescente
          possa acessar os serviços da{' '}
          <Text color={theme?.colors.brand.primary[300]} renderAs="span">
            {patient?.clinic?.name || ' Conexa Saúde'}
          </Text>
          .
        </Text>
        <Text variant="paragraphSmall">
          Garantimos a proteção dos dados e conteúdos adequados para a faixa
          etária.
        </Text>
        <form ref={formRef}>
          <div css={styles.formContainer}>
            <Text variant="subheading">Dados do responsável legal</Text>
            <Input
              label="Nome completo"
              placeholder="Nome completo do responsável"
              ref={form.register('guardianName').ref}
              name={form.register('guardianName').name}
              onBlur={form.register('guardianName').onBlur}
              onChange={form.register('guardianName').onChange}
              maxLength={50}
              fullWidth
              error={form.formState.errors.guardianName?.message?.toString()}
              validated={
                !form?.formState?.errors?.guardianName?.message &&
                form?.formState?.isValid
              }
            />
            <Input
              ref={form.register('guardianCpf').ref}
              name={form.register('guardianCpf').name}
              onBlur={form.register('guardianCpf').onBlur}
              onChange={event => {
                const { value } = event.target
                event.target.value = formatCpfNumber(value)
                form.register('guardianCpf').onChange(event)
              }}
              label="CPF"
              error={form.formState.errors.guardianCpf?.message?.toString()}
              fullWidth
              placeholder="999.999.999-99"
              validated={
                !form?.formState?.errors?.guardianCpf?.message &&
                form?.formState?.isValid
              }
            />
            <div css={styles.containerTerm}>
              <Checkbox
                {...form.register('minorsTerm')}
                onClick={trackMinorsTermClick}
              ></Checkbox>
              <Text variant="label">
                Li e aceito o{' '}
                <Text variant="link" renderAs="span" onClick={openTermUrl}>
                  Termo para crianças e adolescentes.
                </Text>
              </Text>
            </div>
          </div>
        </form>
      </div>
    </Modal>
  )
}

export { MinorsTermModal }
